import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import type { DpEmployeeDetail } from "../api/employeesApi";
import { DpProvisionDetail, getContractProvisionApi, todayIso, variationOf } from "../api/provisionsApi";
import { formatCompetence, formatCurrency, formatDateOnly } from "../utils/format";
import RuleMessages from "./RuleMessages";

type Props = { token: string; employee: DpEmployeeDetail };

const reference = (value: string | null) =>
  value === null ? "—" : Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

/**
 * Aba "Provisões": 13º, férias, 1/3 e FGTS que a empresa já deve ao colaborador na
 * data (padrão hoje), com a memória de cálculo, e as fotos mensais gravadas no
 * fechamento da folha. Só leitura.
 */
export default function EmployeeProvisionsTab({ token, employee }: Props) {
  const [searchParams] = useSearchParams();
  const contracts = employee.contracts;
  const defaultContract = useMemo(
    () => contracts.find((c) => !c.termination) ?? [...contracts].sort((a, b) => b.admissionDate.localeCompare(a.admissionDate))[0] ?? null,
    [contracts]
  );
  const [contractId, setContractId] = useState(defaultContract?.id ?? "");
  const [asOf, setAsOf] = useState(searchParams.get("data") || todayIso());
  const [data, setData] = useState<DpProvisionDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contractId && defaultContract) setContractId(defaultContract.id);
  }, [contractId, defaultContract]);

  useEffect(() => {
    if (!contractId || !asOf) return;
    let alive = true;
    setLoading(true);
    getContractProvisionApi(token, contractId, asOf)
      .then((detail) => alive && setData(detail))
      .catch((error: any) => {
        if (!alive) return;
        toast.error(error?.message || "Erro ao carregar a provisão.");
        setData(null);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token, contractId, asOf]);

  if (contracts.length === 0) {
    return <div className="dpEmpty">Sem contrato: a provisão nasce com o contrato.</div>;
  }

  return (
    <section className="dpSection">
      <div className="dpControls">
        <label className="dpFormGroup" style={{ margin: 0 }}>
          <span className="dpFormHint">Data de referência</span>
          <input className="dpInput" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </label>
        {contracts.length > 1 ? (
          <label className="dpFormGroup" style={{ margin: 0 }}>
            <span className="dpFormHint">Contrato</span>
            <select className="dpSelect" value={contractId} onChange={(e) => setContractId(e.target.value)}>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>
                  Admissão {formatDateOnly(c.admissionDate)}
                  {c.registrationNumber ? ` · matrícula ${c.registrationNumber}` : ""}
                  {c.termination ? " · encerrado" : ""}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <p className="dpNotice">
        Conferência: o que seria devido numa dispensa sem justa causa na data, sem aviso prévio nem multa do FGTS. O INSS da empresa
        não entra.
      </p>

      {loading && !data ? (
        <div className="dpEmpty">Carregando...</div>
      ) : data ? (
        <>
          <RuleMessages errors={data.pending} warnings={data.warnings} />

          <div className="dpCounters">
            {[
              ["Total provisionado", formatCurrency(data.totalAmount)],
              [`13º (${data.summary.thirteenthAvos}/12)`, formatCurrency(data.thirteenthAmount)],
              ["Férias + 1/3", formatCurrency(Number(data.vacationAmount) + Number(data.vacationThirdAmount))],
              ["FGTS", formatCurrency(data.fgtsAmount)],
            ].map(([label, value]) => (
              <div key={label} className="dpCounter">
                <div className="dpCounter__value">{value}</div>
                <div className="dpCounter__label">{label}</div>
              </div>
            ))}
          </div>

          {data.lines.length === 0 ? (
            <div className="dpEmpty">Nada a provisionar nesta data.</div>
          ) : (
            <div className="dpTableWrapper">
              <table className="dpTable">
                <thead>
                  <tr>
                    <th>Verba</th>
                    <th>Referência</th>
                    <th>Valor</th>
                    <th>Como foi calculado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((l) => (
                    <tr key={l.code}>
                      <td>
                        <strong>{l.name}</strong>
                      </td>
                      <td>{reference(l.reference)}</td>
                      <td>{formatCurrency(l.amount)}</td>
                      <td className="dpFormHint" style={{ whiteSpace: "normal" }}>
                        {l.memo}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="dpSection__title">Fotos mensais</h3>
          {data.history.length === 0 ? (
            <div className="dpEmpty">Nenhuma foto ainda. Ela é gravada ao fechar a folha mensal.</div>
          ) : (
            <div className="dpTableWrapper">
              <table className="dpTable">
                <thead>
                  <tr>
                    <th>Competência</th>
                    <th>13º</th>
                    <th>Férias</th>
                    <th>1/3</th>
                    <th>FGTS</th>
                    <th>Total</th>
                    <th>Variação</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((h, i) => {
                    const before = data.history[i + 1];
                    const consecutive = before && before.year * 12 + before.month === h.year * 12 + h.month - 1;
                    const v = consecutive ? variationOf(h.totalAmount, before.totalAmount) : null;
                    return (
                      <tr key={`${h.year}-${h.month}`}>
                        <td>
                          {formatCompetence(h.year, h.month)}
                          {h.pending.length ? <div className="dpFormHint">com pendência</div> : null}
                        </td>
                        <td>{formatCurrency(h.thirteenthAmount)}</td>
                        <td>{formatCurrency(h.vacationAmount)}</td>
                        <td>{formatCurrency(h.vacationThirdAmount)}</td>
                        <td>{formatCurrency(h.fgtsAmount)}</td>
                        <td>
                          <strong>{formatCurrency(h.totalAmount)}</strong>
                        </td>
                        <td>{v ? `${v.diff >= 0 ? "+" : "−"} ${formatCurrency(Math.abs(v.diff))}` : <span className="dpMuted">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
